import { TIME_FORMAT } from '@/constants/date';
import dayjs from 'dayjs';

// 格式化金额，后端返回的是分，转换为元
export function formatPrice(price: number) {
  return price / 100;
}

// 格式化金额，从分转换为元
export function formatPriceCentToYuan(price: number) {
  return price / 100;
}

// 格式化金额，从元转换为分
export function formatPriceYuanToCent(price: number) {
  return Math.round(price * 100);
}

// 格式化后端返回的时间
export function renderTime(value: string, format = TIME_FORMAT.sec) {
  return !value || value === '0001-01-01 00:00:00' ? null : dayjs(value).format(format);
}

/**
 * 格式化金额，添加千分位分隔符
 * @param amount 金额（单位：分）
 * @returns 格式化后的金额字符串（单位：元）
 */
export function formatAmount(amount: number, divisor = 100): string {
  // 将分转换为元
  const yuan = amount / divisor;
  // 如果金额为0，则返回'-'
  if (yuan === 0) return '-';
  // 使用toLocaleString添加千分位分隔符，保留2位小数
  return (
    yuan.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' 元'
  );
}

/**
 * 格式化金额：添加千分位逗号
 * @param amount 金额（单位：分 | 元）
 * @returns 格式化后的金额字符串（单位：元）
 * - 整数：不带小数
 * - 小数：保留两位
 */
export function formatAmountFixedDecimal(amount: number | string, isYuan = false): string {
  let num = Number(amount);
  if (isNaN(num)) {
    return '--';
  }

  num = isYuan ? num : num / 100;

  // 是否为整数
  const isInteger = Number.isInteger(num);

  if (isInteger) {
    return num.toLocaleString('en-US');
  }

  // 保留两位小数（四舍五入）
  const fixed = num.toFixed(2);

  const [integerPart, decimalPart] = fixed.split('.');
  const formattedInteger = Number(integerPart).toLocaleString('en-US');

  return `${formattedInteger}.${decimalPart}`;
}

export function formatRefundAmountDisplay(amount: number | string, isYuan = false): string {
  const absAmount = Math.abs(Number(amount) || 0);
  const formattedAmount = formatAmountFixedDecimal(absAmount, isYuan);

  if (formattedAmount === '--' || absAmount === 0) {
    return `¥${formattedAmount}`;
  }

  return `-¥${formattedAmount}`;
}

/**
 * 清理对象中的空值
 * @param obj 对象
 * @returns 清理后的对象
 */
export function cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined,
    ),
  ) as Partial<T>;
}

/**
 * 获取两个对象之间变化的字段
 * @param newValues 新值对象
 * @param originalValues 原始值对象
 * @returns 包含变化字段的对象和是否有变化的标识
 */
export function getChangedFields<T extends Record<string, any>>(
  newValues: T,
  originalValues: T,
): { changedFields: Record<string, any>; hasChanges: boolean } {
  const changedFields: Record<string, any> = {};
  let hasChanges = false;

  Object.keys(newValues).forEach((key) => {
    if (newValues[key] !== originalValues[key]) {
      changedFields[key] = newValues[key];
      hasChanges = true;
    }
  });

  return { changedFields, hasChanges };
}

/**
 * 除法计算并保留指定小数位（四舍五入）
 * @param n1 被除数
 * @param n2 除数
 * @param decimalPlaces 保留的小数位数（默认 1 位）
 * @returns number
 */
export function divideAndRound(n1: number, n2: number, decimalPlaces = 1): number {
  if (typeof n1 !== 'number' || typeof n2 !== 'number' || n2 === 0) {
    throw new Error('参数错误：请传入有效数字，且除数不能为 0');
  }
  if (decimalPlaces !== 1 && decimalPlaces !== 2) {
    throw new Error('decimalPlaces 只能为 1 或 2');
  }

  const factor = Math.pow(10, decimalPlaces);
  return Math.round((n1 / n2) * factor) / factor;
}

/**
 * 如果数据是空字符串、null 或 undefined，则返回 true，否则返回 false
 * @param value 任意值
 * @returns boolean
 */
export function isEmpty(value: any): boolean {
  return value === '' || value === null || value === undefined;
}
